import type { PublicMailIdentity, PublicMailTemplate } from '@embertime/shared'
import { useQuery } from '@tanstack/react-query'
import { Link } from '@tanstack/react-router'
import { ChevronRight, PanelsTopLeft } from 'lucide-react'
import { api } from '../../lib/api'
import { SidePanel } from '../ui/side-panel'

export function templatesForIdentity<T extends Pick<PublicMailTemplate, 'companyIds'>>(
  templates: T[],
  identity: Pick<PublicMailIdentity, 'companyId'> | undefined,
): T[] {
  return identity?.companyId
    ? templates.filter((template) => template.companyIds.includes(identity.companyId ?? ''))
    : []
}

export function TemplateSheet({
  identity,
  onClose,
  onSelect,
}: {
  identity: PublicMailIdentity | undefined
  onClose: () => void
  onSelect: (template: PublicMailTemplate) => void
}) {
  const { data: firms } = useQuery({ queryKey: ['mail-companies'], queryFn: api.listMailCompanies })
  const { data, isPending } = useQuery({
    queryKey: ['mail-templates-for-identity', identity?.id],
    queryFn: () => api.listMailTemplates({ forIdentity: identity?.id }),
    enabled: !!identity?.id && !!identity.companyId,
  })
  const company = firms?.companies.find((item) => item.id === identity?.companyId)
  return (
    <SidePanel
      open
      onClose={onClose}
      title="Vorlagen"
      className="h-[338px] sm:h-auto"
      description={company ? `Für „${company.name}“` : 'Keine Firma zugeordnet'}
      footer={
        <Link
          to="/settings/mail-accounts"
          hash="mail-templates"
          onClick={onClose}
          className="block w-full py-3 text-center text-sm text-text-secondary"
        >
          Vorlagen verwalten
        </Link>
      }
    >
      <div className="-mx-4 divide-y divide-border-subtle">
        {!identity?.companyId && (
          <p className="p-4 text-sm text-text-secondary">
            Dieser Absender hat noch kein Firmenprofil.
          </p>
        )}
        {identity?.companyId && isPending && (
          <p className="p-4 text-sm text-text-secondary">Vorlagen werden geladen …</p>
        )}
        {identity?.companyId && !isPending && data?.templates.length === 0 && (
          <p className="p-4 text-sm text-text-secondary">Keine Vorlage für diese Firma.</p>
        )}
        {templatesForIdentity(data?.templates ?? [], identity).map((template) => (
          <button
            key={template.id}
            type="button"
            onClick={() => onSelect(template)}
            className="flex min-h-[56px] w-full items-center gap-2 px-3 py-2 text-left hover:bg-bg-overlay"
          >
            <span className="rounded-lg bg-bg-overlay p-2 text-text-secondary">
              <PanelsTopLeft size={18} />
            </span>
            <span className="min-w-0 flex-1">
              <span className="flex flex-wrap items-center gap-x-1 gap-y-0.5 text-sm font-semibold">
                {template.title}{' '}
                <span className="inline-flex max-w-full items-center gap-1 rounded-full bg-accent/10 px-1.5 py-0.5 text-[10px] font-normal text-accent">
                  <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-accent" />
                  {company?.name}
                </span>
              </span>
              <span className="block truncate text-xs text-text-tertiary">{template.bodyText}</span>
            </span>
            <ChevronRight size={16} className="text-text-tertiary" />
          </button>
        ))}
      </div>
    </SidePanel>
  )
}
